'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import { UtensilsCrossed, MapPin, ShoppingBag, Plus, Minus, X, Check, Download, Globe, Phone, Search, ChevronRight, ArrowLeft } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { createOrder, type OrderItem, type OrderType } from '@/lib/db/orders'
import { DEFAULT_ORDERS_SETTINGS, type OrdersSettings } from '@/lib/db/settings'
import { cdn } from '@/lib/cdn'

interface DisplayItem {
  id: string
  name: string
  priceStr: string
  priceNum: number
  desc: string
  allergens: string[]
  labels: string[]
  image: string | null
}
interface DisplayCat { id: string; name: string; items: DisplayItem[] }

const LABEL_ICONS: Record<string, string> = {
  vegan: '🌱', vegetarisch: '🥗', scharf: '🌶️', bio: '🌿',
  beliebt: '⭐', neu: '🆕', glutenfrei: '🌾', laktosefrei: '🥛',
}

interface RestaurantInfo {
  id: string | null
  name: string
  hero_image_url: string | null
  primary_color: string
  phone: string | null
  address: string | null
  city: string | null
  zip_code: string | null
  orders_enabled: boolean
  orders_pickup_enabled: boolean
  orders_delivery_enabled: boolean
  menu_pdf_url: string | null
}

interface CartLine {
  itemId: string
  name: string
  price: number
  quantity: number
}

function fmtPrice(n: number) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n)
}

function parsePrice(s: string): number {
  if (!s) return 0
  const cleaned = s.replace(/[^\d,.\-]/g, '').replace(',', '.')
  const n = parseFloat(cleaned)
  return Number.isFinite(n) ? n : 0
}

// Display a price the restaurant typed (could be "5", "5,90", "5,90 €", etc.)
// in canonical "X,XX €" form. Falls back to the raw string if no number found.
function displayPrice(raw: string): string {
  const n = parsePrice(raw)
  if (n <= 0) return raw || ''
  return fmtPrice(n)
}

// lucide dropped brand icons — minimal Instagram glyph, stroke-styled to
// match the neighbouring Globe/Phone icons.
function InstagramIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <rect x="2.5" y="2.5" width="19" height="19" rx="5" />
      <circle cx="12" cy="12" r="4.2" />
      <circle cx="17.3" cy="6.7" r="0.9" fill="currentColor" stroke="none" />
    </svg>
  )
}

// UI strings for the cards design's DE/EN toggle. Menu CONTENT stays in the
// language the restaurant typed it — this only translates the chrome.
const CARDS_T = {
  de: { search: 'Karte durchsuchen', overview: 'Übersicht', noResults: 'Keine Treffer', menuLabel: 'Speisekarte' },
  en: { search: 'Search the menu', overview: 'Overview', noResults: 'No results', menuLabel: 'Menu' },
} as const

export default function PublicMenuPage() {
  const params = useParams()
  const slug = (params.restaurant as string) ?? ''
  // Dine-in: a table QR code links here with ?table=<n> (or ?tisch=). When
  // present, the order is placed "Vor Ort" for that table, paid in cash at
  // the table — no online payment, no address.
  const search = useSearchParams()
  const tableNumber = ((search?.get('table') ?? search?.get('tisch') ?? '').trim().slice(0, 20)) || null
  const [info, setInfo] = useState<RestaurantInfo>({
    id: null,
    name: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
    hero_image_url: null, primary_color: '#c9a96e',
    phone: null, address: null, city: null, zip_code: null,
    orders_enabled: false, orders_pickup_enabled: false, orders_delivery_enabled: false,
    menu_pdf_url: null,
  })
  const [menu, setMenu] = useState<DisplayCat[]>([])
  const [loaded, setLoaded] = useState(false)
  const [activeCat, setActiveCat] = useState<string | null>(null)
  const [orderSettings, setOrderSettings] = useState<OrdersSettings>(DEFAULT_ORDERS_SETTINGS)
  // Cards design (Menury-style): landing = photo category grid + contact
  // icons + search + DE/EN toggle; tapping a card drops into the classic
  // list view. 'classic' restaurants never see any of this.
  const [cardsView, setCardsView] = useState<'home' | 'group' | 'list'>('home')
  // 'grouped' design: the Hauptkategorie the guest is currently inside.
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [menuLang, setMenuLang] = useState<'de' | 'en'>('de')
  const [query, setQuery] = useState('')
  // Machine translation of the CONTENT (dish/category names + descriptions),
  // fetched once when the guest switches to EN; served from a server-side
  // cache after the first ever request per restaurant.
  const [trans, setTrans] = useState<{ cats: Record<string, string>; items: Record<string, { name: string; desc: string }>; groups?: Record<string, string> } | null>(null)
  // Guard via ref, NOT state: putting a loading flag in the deps re-ran the
  // effect the moment it set the flag, whose cleanup set cancelled=true and
  // silently dropped the fetch result — EN never applied despite a 200.
  const transStartedRef = useRef(false)
  // Cached EN translations that arrived with the /api/menu/snapshot payload —
  // used as a fallback when /api/menu/translate can't run (Supabase outage).
  const snapTransRef = useRef<{ cats: Record<string, string>; items: Record<string, { name: string; desc: string }>; groups?: Record<string, string> } | null>(null)

  useEffect(() => {
    if (menuLang !== 'en' || !loaded || transStartedRef.current) return
    transStartedRef.current = true
    ;(async () => {
      try {
        const r = await fetch('/api/menu/translate', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug, lang: 'en' }),
        })
        if (!r.ok) throw new Error(String(r.status))
        setTrans(await r.json())
      } catch {
        if (snapTransRef.current) setTrans(snapTransRef.current) // stale EN beats German-only
        else transStartedRef.current = false /* retry on next EN switch */
      }
    })()
  }, [menuLang, loaded, slug])

  const tCatName = (id: string, fallback: string) => (menuLang === 'en' && trans?.cats[id]) || fallback
  const tGroupName = (id: string, fallback: string) => (menuLang === 'en' && trans?.groups?.[id]) || fallback
  const tItemName = (item: DisplayItem) => (menuLang === 'en' && trans?.items[item.id]?.name) || item.name
  const tItemDesc = (item: DisplayItem) => (menuLang === 'en' ? (trans?.items[item.id]?.desc || item.desc) : item.desc)

  function openCategory(catId: string) {
    // Grouped design: entering a category also enters its Hauptkategorie so
    // the list + bottom nav scope to that group (search results, pills…).
    const gid = (orderSettings.category_groups ?? {})[catId]
    setActiveGroup(gid && (orderSettings.menu_groups ?? []).some(g => g.id === gid) ? gid : null)
    setActiveCat(catId)
    setCardsView('list')
    setQuery('')
    setTimeout(() => {
      document.getElementById(`cat-${catId}`)?.scrollIntoView({ behavior: 'auto', block: 'start' })
    }, 60)
  }

  // Scroll-spy: as the guest scrolls, the bottom nav highlights the category
  // currently being read. Deterministic rule (an IntersectionObserver only
  // reports CHANGED entries, which mis-picked at section boundaries): the
  // active category is the LAST one whose heading has passed the top ~30%
  // of the viewport — and at the very bottom of the page, the last category.
  useEffect(() => {
    if (!loaded || menu.length === 0) return
    if (orderSettings.menu_design !== 'classic' && cardsView !== 'list') return
    let ticking = false
    const compute = () => {
      ticking = false
      if (listCats.length === 0) return
      const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 8
      if (atBottom) {
        setActiveCat(listCats[listCats.length - 1].id)
        return
      }
      const band = window.innerHeight * 0.3
      let current: string | null = null
      for (const c of listCats) {
        const el = document.getElementById(`cat-${c.id}`)
        if (!el) continue
        if (el.getBoundingClientRect().top <= band) current = c.id
        else break
      }
      if (current) setActiveCat(current)
    }
    const onScroll = () => { if (!ticking) { ticking = true; requestAnimationFrame(compute) } }
    window.addEventListener('scroll', onScroll, { passive: true })
    compute()
    return () => window.removeEventListener('scroll', onScroll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, menu, orderSettings.menu_design, cardsView, activeGroup])

  // …and the active pill auto-centers in the horizontally scrolling bar.
  useEffect(() => {
    if (!activeCat) return
    const pill = document.getElementById(`catpill-${activeCat}`)
    const nav = pill?.parentElement
    if (pill && nav) {
      nav.scrollTo({ left: pill.offsetLeft - nav.clientWidth / 2 + pill.clientWidth / 2, behavior: 'smooth' })
    }
  }, [activeCat])

  // Intro splash: fullscreen logo animation, once per browser session,
  // always skippable. Never shown for reduced-motion users.
  const [showIntro, setShowIntro] = useState(false)

  function dismissIntro() {
    try { sessionStorage.setItem(`rzr-intro-${slug}`, '1') } catch { /* private mode */ }
    setShowIntro(false)
  }

  // Safety cap: whatever happens with the video, the menu appears after 8s.
  useEffect(() => {
    if (!showIntro) return
    const t = setTimeout(dismissIntro, 8000)
    return () => clearTimeout(t)
  }, [showIntro]) // eslint-disable-line react-hooks/exhaustive-deps

  // Cart
  const [cart, setCart] = useState<CartLine[]>([])
  const [cartOpen, setCartOpen] = useState(false)
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [orderConfirmed, setOrderConfirmed] = useState<{ number: number } | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // All menu data comes from our own VPS endpoint: it proxies Supabase
        // and keeps a disk snapshot, so the QR link keeps working (stale data,
        // same look) even when Supabase itself is down or quota-restricted.
        const r = await fetch(`/api/menu/snapshot?slug=${encodeURIComponent(slug)}`, { cache: 'no-store' })
        if (!r.ok) { if (!cancelled) setLoaded(true); return }
        const snap = await r.json() as {
          info: { id: string; name: string | null; hero_image_url: string | null; primary_color: string | null; phone: string | null; address: string | null; city: string | null; zip_code: string | null; orders_enabled: boolean; orders_pickup_enabled: boolean; orders_delivery_enabled: boolean; menu_pdf_url: string | null }
          orders_settings: Record<string, unknown> | null
          categories: { id: string; name: string }[]
          items: { id: string; category_id: string; name: string; description: string | null; price: string; allergens: string[]; labels: string[]; image_url: string | null }[]
          translations: { cats: Record<string, string>; items: Record<string, { name: string; desc: string }> } | null
        }
        if (cancelled || !snap?.info?.id) { setLoaded(true); return }
        const rest = snap.info
        setInfo({
          id: rest.id,
          name: rest.name ?? info.name,
          hero_image_url: rest.hero_image_url,
          primary_color: rest.primary_color ?? '#c9a96e',
          phone: rest.phone, address: rest.address, city: rest.city, zip_code: rest.zip_code,
          orders_enabled: !!rest.orders_enabled,
          orders_pickup_enabled: !!rest.orders_pickup_enabled,
          orders_delivery_enabled: !!rest.orders_delivery_enabled,
          menu_pdf_url: rest.menu_pdf_url ?? null,
        })
        snapTransRef.current = snap.translations ?? null
        if (snap.orders_settings && typeof snap.orders_settings === 'object') {
          const os = { ...DEFAULT_ORDERS_SETTINGS, ...(snap.orders_settings as object) }
          setOrderSettings(os)
          try {
            // ?intro=1 forces the splash (testing/preview) past the
            // once-per-session guard and reduced-motion.
            const force = new URLSearchParams(window.location.search).get('intro') === '1'
            const seen = sessionStorage.getItem(`rzr-intro-${slug}`)
            const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
            if (os.intro_video_url && (force || (!seen && !reduced))) setShowIntro(true)
          } catch { /* private mode — skip the intro */ }
        }
        const itemsByCategory = new Map<string, DisplayItem[]>()
        for (const it of snap.items ?? []) {
          const arr = itemsByCategory.get(it.category_id) ?? []
          arr.push({
            id: it.id, name: it.name,
            priceStr: it.price,
            priceNum: parsePrice(it.price),
            desc: it.description ?? '',
            allergens: it.allergens ?? [],
            labels: (it.labels ?? []).map(l => LABEL_ICONS[l] ?? l),
            image: it.image_url,
          })
          itemsByCategory.set(it.category_id, arr)
        }
        const finalMenu = (snap.categories ?? [])
          .map(c => ({ id: c.id, name: c.name, items: itemsByCategory.get(c.id) ?? [] }))
          .filter(c => c.items.length > 0)
        setMenu(finalMenu)
        if (finalMenu.length > 0) setActiveCat(finalMenu[0].id)
      } catch { /* ignore */ }
      finally { if (!cancelled) setLoaded(true) }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  const accent = info.primary_color
  const isGrouped = orderSettings.menu_design === 'grouped'
  // Grouped shares the whole cards chrome (search, contact icons, DE/EN,
  // card grids) — it only adds a Hauptkategorie level above the categories.
  const isCards = orderSettings.menu_design === 'cards' || isGrouped
  const groups = isGrouped
    ? (orderSettings.menu_groups ?? [])
        .map(g => ({ ...g, cats: menu.filter(c => (orderSettings.category_groups ?? {})[c.id] === g.id) }))
        .filter(g => g.cats.length > 0)
    : []
  const groupedCatIds = new Set(groups.flatMap(g => g.cats.map(c => c.id)))
  const standaloneCats = isGrouped ? menu.filter(c => !groupedCatIds.has(c.id)) : menu
  const activeGroupData = groups.find(g => g.id === activeGroup) ?? null
  // Inside a Hauptkategorie the LIST shows only that group's categories —
  // sections, scroll-spy and the bottom-nav pills all share this subset.
  const listCats = isGrouped && activeGroupData ? activeGroupData.cats : menu
  // Owner-chosen cards-per-row for tablet/desktop (phones always 2).
  // Tailwind needs the class literals spelled out to ship them in the CSS.
  const cardGridCls = orderSettings.menu_card_cols === 2
    ? 'grid grid-cols-2 gap-4 sm:gap-5'
    : orderSettings.menu_card_cols === 4
      ? 'grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5'
      : 'grid grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5'
  const ct = CARDS_T[menuLang]
  const rawLinks = orderSettings.menu_links ?? { website: '', instagram: '' }
  // Owners paste "instagram.com/…" without a scheme — normalise so the icon
  // links out instead of resolving relative to rezaro.de.
  const normUrl = (u: string) => (!u ? '' : /^https?:\/\//i.test(u) ? u : `https://${u.replace(/^@/, 'instagram.com/')}`)
  const menuLinks = { website: normUrl(rawLinks.website), instagram: normUrl(rawLinks.instagram) }
  const q = query.trim().toLowerCase()
  const searchResults = q
    ? menu.flatMap(cat => cat.items
        .filter(i => `${i.name} ${i.desc ?? ''}`.toLowerCase().includes(q))
        .map(item => ({ item, catId: cat.id, catName: cat.name })))
      .slice(0, 30)
    : []
  const hasItemImages = menu.some(c => c.items.some(i => !!i.image))
  const orderingAvailable = info.orders_enabled && (info.orders_pickup_enabled || info.orders_delivery_enabled)

  const cartCount = cart.reduce((s, c) => s + c.quantity, 0)
  const cartSubtotal = cart.reduce((s, c) => s + c.price * c.quantity, 0)

  function addToCart(item: DisplayItem) {
    if (item.priceNum <= 0) return
    setCart(prev => {
      const existing = prev.find(p => p.itemId === item.id)
      if (existing) return prev.map(p => p.itemId === item.id ? { ...p, quantity: p.quantity + 1 } : p)
      return [...prev, { itemId: item.id, name: item.name, price: item.priceNum, quantity: 1 }]
    })
  }

  function changeQty(itemId: string, delta: number) {
    setCart(prev => prev.flatMap(p => {
      if (p.itemId !== itemId) return [p]
      const q = p.quantity + delta
      return q <= 0 ? [] : [{ ...p, quantity: q }]
    }))
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;500;600;700&family=Inter:wght@300;400;500;600&display=swap');

        .menu-root { font-family: 'Inter', sans-serif; background: #fafaf7; min-height: 100vh; -webkit-tap-highlight-color: transparent; }
        /* Touch: no blue tap flash / accidental text selection on the dish
           cards, and the hover-lift only where a real pointer exists. */
        .menu-card, .cat-pill { -webkit-user-select: none; user-select: none; }
        @media (hover: none) {
          .menu-card:hover { transform: none; box-shadow: none; border-color: #ede9e0; }
        }
        .menu-display { font-family: 'Cormorant Garamond', serif; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }
        .anim-fadeup { animation: fadeUp 0.5s cubic-bezier(.22,1,.36,1) both; }
        .anim-fadein { animation: fadeIn 0.6s ease both; }
        .anim-slideup { animation: slideUp 0.3s cubic-bezier(.22,1,.36,1) both; }

        .hero-overlay { background: linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.65) 100%); }

        .menu-card { transition: all 0.25s cubic-bezier(.22,1,.36,1); background: #fff; border: 1px solid #ede9e0; }
        .menu-card:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(0,0,0,0.08); border-color: #d4c4a0; }

        .cat-pill { transition: all 0.18s; cursor: pointer; }
        .cat-pill.active { background: var(--accent); color: white; border-color: var(--accent); }

        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }

        .label-pill {
          display: inline-flex; align-items: center; gap: 3px;
          font-size: 11px; font-weight: 500;
          padding: 2px 7px; border-radius: 999px;
          background: rgba(0,0,0,0.04);
        }

        .allergen-dot {
          display: inline-flex; align-items: center; justify-content: center;
          width: 17px; height: 17px; border-radius: 50%;
          background: #f3f0e8; color: #8b7355;
          font-size: 9px; font-weight: 700;
        }

        /* Prices in the UI sans (not the serif display face) — Cormorant's
           thin numerals read poorly at small sizes on phones. Tabular nums
           keep the euro amounts visually aligned down the list. */
        .price-tag { font-family: 'Inter', sans-serif; font-weight: 600; font-size: 14.5px; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; white-space: nowrap; }

        .section-title { font-family: 'Cormorant Garamond', serif; font-weight: 600; }
        .section-divider { width: 28px; height: 2px; margin-top: 4px; margin-bottom: 18px; }

        .qty-btn {
          width: 28px; height: 28px; border-radius: 50%;
          display: inline-flex; align-items: center; justify-content: center;
          background: var(--accent); color: white;
          transition: transform 0.15s;
        }
        .qty-btn:hover { transform: scale(1.08); }
        .qty-btn:active { transform: scale(0.94); }
        .qty-btn.outline { background: transparent; color: var(--accent); border: 1px solid var(--accent); }
      `}</style>

      {/* Intro splash — plays the owner's logo animation once per session.
          Hard 8s cap + tap-to-skip so a stalled video can never trap the
          guest away from the menu. */}
      {showIntro && orderSettings.intro_video_url && (
        <div className="fixed inset-0 z-[100] bg-black flex items-center justify-center" onClick={dismissIntro}>
          {/* React does not render the `muted` ATTRIBUTE (property only) —
              without it iOS Safari refuses to autoplay, so the intro never
              appeared there. The callback ref forces muted + play(). No
              visible skip button per owner: tap anywhere still dismisses,
              and the 8s cap + onEnded/onError guards remain. */}
          <video
            ref={(el) => { if (el) { el.muted = true; el.play()?.catch(() => { /* blocked → 8s cap closes */ }) } }}
            src={cdn(orderSettings.intro_video_url)}
            autoPlay muted playsInline preload="auto"
            onEnded={dismissIntro}
            onError={dismissIntro}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      )}

      <div className="menu-root" style={{ ['--accent' as string]: accent } as React.CSSProperties}>
        {/* Hero */}
        <div className="relative h-[200px] sm:h-[240px] overflow-hidden anim-fadein"
          style={info.hero_image_url
            ? { backgroundImage: `url(${cdn(info.hero_image_url)})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { background: `linear-gradient(135deg, ${accent}88 0%, #1a1a18 100%)` }}>
          {/* Overlay exists to keep the header text readable — when the
              header is hidden the banner shows at full brightness. */}
          {orderSettings.menu_show_header !== false && <div className="absolute inset-0 hero-overlay" />}
          {isCards && (
            <div className="absolute top-4 right-4 z-20 flex rounded-full bg-white/90 backdrop-blur overflow-hidden text-[12px] font-bold shadow-sm">
              {(['de', 'en'] as const).map(l => (
                <button key={l} type="button" onClick={() => setMenuLang(l)}
                  className={`px-3 py-1.5 transition-colors ${menuLang === l ? 'text-white' : 'text-neutral-600'}`}
                  style={menuLang === l ? { background: accent } : undefined}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}
          <div className="relative z-10 max-w-2xl mx-auto px-5 h-full flex flex-col justify-end pb-6">
            {orderSettings.menu_show_header !== false && (
              <>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 mb-1">{isCards ? ct.menuLabel : 'Speisekarte'}</p>
                <h1 className="menu-display text-3xl sm:text-4xl text-white font-semibold leading-tight" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.3)' }}>
                  {info.name}
                </h1>
                {info.address && (
                  <p className="text-xs text-white/70 mt-1.5 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />{info.address}{info.city ? `, ${info.city}` : ''}
                  </p>
                )}
              </>
            )}
            {orderSettings.menu_show_header === false && <h1 className="sr-only">{info.name}</h1>}
            {info.menu_pdf_url && (
              <a
                href={info.menu_pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-white/95 px-3.5 py-1.5 text-[12px] font-semibold text-neutral-900 hover:bg-white shadow-sm transition-colors"
                style={{ color: accent }}>
                <Download className="h-3.5 w-3.5" />
                Speisekarte als PDF
              </a>
            )}
          </div>
        </div>

        {/* Cards design — landing: contact icons, search, category grid */}
        {isCards && cardsView === 'home' && loaded && menu.length > 0 && (
          <div className="max-w-2xl mx-auto px-4 pb-20 pt-7">
            {(menuLinks.website || info.phone || menuLinks.instagram) && (
              <div className="flex items-center justify-center gap-8 mb-7">
                {menuLinks.website && (
                  <a href={menuLinks.website} target="_blank" rel="noopener noreferrer" aria-label="Website"
                    className="text-neutral-700 hover:opacity-60 transition-opacity"><Globe className="h-6 w-6" strokeWidth={1.6} /></a>
                )}
                {info.phone && (
                  <a href={`tel:${info.phone}`} aria-label="Telefon"
                    className="text-neutral-700 hover:opacity-60 transition-opacity"><Phone className="h-6 w-6" strokeWidth={1.6} /></a>
                )}
                {menuLinks.instagram && (
                  <a href={menuLinks.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                    className="text-neutral-700 hover:opacity-60 transition-opacity"><InstagramIcon className="h-6 w-6" /></a>
                )}
              </div>
            )}

            <div className="flex items-center gap-2.5 border-b border-neutral-300 pb-2.5 mb-8 max-w-md mx-auto">
              <Search className="h-4 w-4 text-neutral-400 flex-shrink-0" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={ct.search}
                className="flex-1 bg-transparent text-[15px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none" />
              {query && (
                <button type="button" onClick={() => setQuery('')} className="text-neutral-400"><X className="h-4 w-4" /></button>
              )}
            </div>

            {q ? (
              <div className="space-y-2 max-w-md mx-auto">
                {searchResults.length === 0 && (
                  <p className="text-center text-sm text-neutral-400 py-10">{ct.noResults}</p>
                )}
                {searchResults.map(({ item, catId, catName }) => (
                  <button key={item.id} type="button" onClick={() => openCategory(catId)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl bg-white border border-neutral-200 px-4 py-3 text-left hover:border-neutral-300 transition-colors">
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-neutral-900 truncate">{tItemName(item)}</span>
                      <span className="block text-[11px] text-neutral-500">{tCatName(catId, catName)}</span>
                    </span>
                    <span className="price-tag flex-shrink-0" style={{ color: accent }}>{displayPrice(item.priceStr)}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div className={cardGridCls}>
                {/* grouped design: Hauptkategorie cards first, then any
                    categories not assigned to a group as regular cards. */}
                {isGrouped && groups.map((g, gi) => {
                  const cover = orderSettings.group_images?.[g.id]
                    || g.cats
                      .map(c => orderSettings.category_images?.[c.id] || c.items.find(i => i.image)?.image)
                      .find(Boolean)
                  return (
                    <button key={g.id} type="button"
                      onClick={() => { setActiveGroup(g.id); setCardsView('group'); window.scrollTo({ top: 0 }) }}
                      className="anim-fadeup rounded-2xl bg-white border border-neutral-200 overflow-hidden text-left shadow-sm hover:shadow-md transition-shadow"
                      style={{ animationDelay: `${gi * 0.05}s` }}>
                      {cover ? (
                        <img src={cdn(cover)} alt="" loading="lazy" decoding="async" className="h-36 sm:h-44 w-full object-cover" />
                      ) : (
                        <div className="h-36 sm:h-44 w-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${accent}1f 0%, ${accent}4d 100%)` }}>
                          <span className="menu-display text-5xl font-semibold" style={{ color: accent }}>{g.name.charAt(0)}</span>
                        </div>
                      )}
                      <div className="px-2.5 h-[54px] flex items-center justify-center gap-1">
                        <span className="text-[14px] font-medium text-neutral-800 tracking-wide text-center leading-tight line-clamp-2">{tGroupName(g.id, g.name)}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-neutral-300 flex-shrink-0" />
                      </div>
                    </button>
                  )
                })}
                {(isGrouped ? standaloneCats : menu).map((cat, ci) => {
                  const cover = orderSettings.category_images?.[cat.id] || cat.items.find(i => i.image)?.image
                  return (
                    <button key={cat.id} type="button" onClick={() => openCategory(cat.id)}
                      className="anim-fadeup rounded-2xl bg-white border border-neutral-200 overflow-hidden text-left shadow-sm hover:shadow-md transition-shadow"
                      style={{ animationDelay: `${(ci + groups.length) * 0.05}s` }}>
                      {cover ? (
                        <img src={cdn(cover)} alt="" loading="lazy" decoding="async" className="h-36 sm:h-44 w-full object-cover" />
                      ) : (
                        <div className="h-36 sm:h-44 w-full flex items-center justify-center"
                          style={{ background: `linear-gradient(135deg, ${accent}1f 0%, ${accent}4d 100%)` }}>
                          <span className="menu-display text-5xl font-semibold" style={{ color: accent }}>{cat.name.charAt(0)}</span>
                        </div>
                      )}
                      {/* Fixed-height label: long names wrap to 2 lines without
                          changing the card height, so images stay aligned. */}
                      <div className="px-2.5 h-[54px] flex items-center justify-center gap-1">
                        <span className="text-[13.5px] text-neutral-800 tracking-wide text-center leading-tight line-clamp-2">{tCatName(cat.id, cat.name)}</span>
                        <ChevronRight className="h-3.5 w-3.5 text-neutral-300 flex-shrink-0" />
                      </div>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}

        {/* grouped design — inside a Hauptkategorie: its sub-categories */}
        {isGrouped && cardsView === 'group' && activeGroupData && (
          <div className="max-w-2xl mx-auto px-4 pb-20 pt-6">
            <button type="button"
              onClick={() => { setActiveGroup(null); setCardsView('home'); window.scrollTo({ top: 0 }) }}
              className="mb-5 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13.5px] font-bold text-white shadow-md hover:opacity-90 transition-opacity"
              style={{ background: accent }}>
              <ChevronRight className="h-4 w-4 rotate-180" strokeWidth={2.6} /> {ct.overview}
            </button>
            <h2 className="menu-display text-3xl font-semibold text-neutral-900 mb-6 text-center">{tGroupName(activeGroupData.id, activeGroupData.name)}</h2>
            <div className="grid grid-cols-2 gap-4 sm:gap-5">
              {activeGroupData.cats.map((cat, ci) => {
                const cover = orderSettings.category_images?.[cat.id] || cat.items.find(i => i.image)?.image
                return (
                  <button key={cat.id} type="button" onClick={() => openCategory(cat.id)}
                    className="anim-fadeup rounded-2xl bg-white border border-neutral-200 overflow-hidden text-left shadow-sm hover:shadow-md transition-shadow"
                    style={{ animationDelay: `${ci * 0.05}s` }}>
                    {cover ? (
                      <img src={cdn(cover)} alt="" loading="lazy" decoding="async" className="h-36 sm:h-44 w-full object-cover" />
                    ) : (
                      <div className="h-36 sm:h-44 w-full flex items-center justify-center"
                        style={{ background: `linear-gradient(135deg, ${accent}1f 0%, ${accent}4d 100%)` }}>
                        <span className="menu-display text-5xl font-semibold" style={{ color: accent }}>{cat.name.charAt(0)}</span>
                      </div>
                    )}
                    <div className="px-3 py-3.5 flex items-center justify-center gap-1">
                      <span className="text-[15px] text-neutral-800 tracking-wide text-center leading-snug">{tCatName(cat.id, cat.name)}</span>
                      <ChevronRight className="h-3.5 w-3.5 text-neutral-300 flex-shrink-0" />
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {/* Content */}
        {(!isCards || cardsView === 'list' || !loaded || menu.length === 0) && (
        <div className="max-w-2xl mx-auto px-4 pb-32 pt-6 space-y-12">
          {!loaded ? (
            <p className="text-center text-sm text-neutral-400 py-20">Speisekarte wird geladen...</p>
          ) : menu.length === 0 ? (
            <div className="text-center py-20">
              <UtensilsCrossed className="h-10 w-10 mx-auto text-neutral-300 mb-3" />
              <p className="text-sm text-neutral-500">Noch keine Speisekarte verfügbar.</p>
            </div>
          ) : listCats.map((cat, ci) => (
            <section key={cat.id} id={`cat-${cat.id}`} className="anim-fadeup" style={{ animationDelay: `${ci * 0.06}s` }}>
              <h2 className="section-title text-2xl text-neutral-900">{tCatName(cat.id, cat.name)}</h2>
              {orderSettings.category_descriptions?.[cat.id] && (
                <p className="mt-1 text-[13px] text-neutral-500 leading-relaxed">
                  {tCatName(`d_${cat.id}`, orderSettings.category_descriptions[cat.id])}
                </p>
              )}
              <div className="section-divider" style={{ background: `linear-gradient(90deg, ${accent}, transparent)` }} />

              <div className="space-y-3">
                {cat.items.map((item, ii) => {
                  const inCart = cart.find(c => c.itemId === item.id)
                  return (
                    <div
                      key={item.id}
                      className="menu-card anim-fadeup rounded-2xl overflow-hidden"
                      style={{ animationDelay: `${ci * 0.06 + ii * 0.04}s` }}
                    >
                      <div className="flex items-start">
                        {item.image ? (
                          <img src={cdn(item.image)} alt={item.name} loading="lazy" decoding="async" width={112} height={112} className="h-24 w-24 sm:h-28 sm:w-28 object-cover flex-shrink-0" />
                        ) : hasItemImages ? (
                          <div className="h-24 w-24 sm:h-28 sm:w-28 flex-shrink-0 flex items-center justify-center" style={{ background: '#f5f1e8' }}>
                            <UtensilsCrossed className="h-6 w-6 text-neutral-300" />
                          </div>
                        ) : null}

                        <div className="flex-1 min-w-0 p-4">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                              <p className="text-[15px] font-semibold text-neutral-900">{tItemName(item)}</p>
                              {item.labels.map((l, i) => (
                                <span key={i} className="text-[14px] leading-none">{l}</span>
                              ))}
                            </div>
                            <span className="price-tag flex-shrink-0" style={{ color: accent }}>{displayPrice(item.priceStr)}</span>
                          </div>
                          {item.desc && (
                            <p className="text-[12.5px] text-neutral-500 mt-1.5 leading-relaxed">{tItemDesc(item)}</p>
                          )}
                          {(orderSettings.item_options?.[item.id]?.length ?? 0) > 0 && (
                            <ul className="mt-2 space-y-1">
                              {(orderSettings.item_options?.[item.id] ?? []).map((opt, oi) => (
                                <li key={oi} className="flex items-start gap-1.5 text-[12px] text-neutral-600 leading-snug">
                                  <span className="mt-[6px] h-1 w-1 rounded-full flex-shrink-0" style={{ background: accent }} />
                                  <span>{opt}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                          {item.allergens.length > 0 && (
                            <div className="flex items-center gap-1 mt-2.5 flex-wrap">
                              <span className="text-[10px] mr-0.5 text-neutral-400">Allergene:</span>
                              {item.allergens.map(a => <span key={a} className="allergen-dot">{a}</span>)}
                            </div>
                          )}
                          {orderingAvailable && item.priceNum > 0 && (
                            <div className="mt-3 flex items-center justify-end gap-2">
                              {inCart ? (
                                <>
                                  <button type="button" onClick={() => changeQty(item.id, -1)} className="qty-btn outline">
                                    <Minus className="h-3.5 w-3.5" />
                                  </button>
                                  <span className="text-sm font-semibold w-6 text-center">{inCart.quantity}</span>
                                  <button type="button" onClick={() => changeQty(item.id, 1)} className="qty-btn">
                                    <Plus className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              ) : (
                                <button type="button" onClick={() => addToCart(item)}
                                  className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                                  style={{ background: accent, color: 'white' }}>
                                  <Plus className="h-3 w-3" />Hinzufügen
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </section>
          ))}
        </div>
        )}

        {/* Floating Cart Button */}
        {orderingAvailable && cartCount > 0 && (
          <button type="button" onClick={() => setCartOpen(true)}
            className="fixed bottom-20 right-4 z-30 flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-2xl"
            style={{ background: accent, animation: 'fadeUp 0.3s' }}>
            <ShoppingBag className="h-4 w-4" />
            <span>{cartCount} {menuLang === 'en' ? 'items' : 'Artikel'}</span>
            <span>·</span>
            <span>{fmtPrice(cartSubtotal)}</span>
          </button>
        )}

        {/* Cart Sheet */}
        {cartOpen && (
          <div className="fixed inset-0 z-50 flex items-end justify-center anim-fadein">
            <div className="absolute inset-0 bg-black/40" onClick={() => setCartOpen(false)} />
            <div className="relative w-full max-w-md bg-white rounded-t-3xl p-6 anim-slideup max-h-[85vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="menu-display text-2xl font-bold">Ihr Warenkorb</h2>
                <button type="button" onClick={() => setCartOpen(false)} className="rounded-full p-1.5 hover:bg-neutral-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              {cart.length === 0 ? (
                <p className="text-center py-12 text-neutral-400 text-sm">Ihr Warenkorb ist leer.</p>
              ) : (
                <>
                  <div className="space-y-3 mb-4">
                    {cart.map((c) => (
                      <div key={c.itemId} className="flex items-center justify-between gap-3 py-2 border-b border-neutral-100">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-neutral-900 truncate">{c.name}</p>
                          <p className="text-xs text-neutral-500">{fmtPrice(c.price)} × {c.quantity}</p>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button type="button" onClick={() => changeQty(c.itemId, -1)} className="qty-btn outline">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="text-sm font-semibold w-5 text-center">{c.quantity}</span>
                          <button type="button" onClick={() => changeQty(c.itemId, 1)} className="qty-btn">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="text-sm font-semibold w-16 text-right">{fmtPrice(c.price * c.quantity)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between text-base font-bold border-t-2 pt-3 border-neutral-900 mb-4">
                    <span>Zwischensumme</span>
                    <span>{fmtPrice(cartSubtotal)}</span>
                  </div>
                  <button type="button" onClick={() => { setCartOpen(false); setCheckoutOpen(true) }}
                    className="w-full rounded-xl py-3 text-white font-semibold text-sm"
                    style={{ background: accent }}>
                    Zur Kasse
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Checkout */}
        {checkoutOpen && info.id && (
          <CheckoutSheet
            restaurantId={info.id}
            cart={cart}
            cartSubtotal={cartSubtotal}
            accent={accent}
            settings={orderSettings}
            pickupEnabled={info.orders_pickup_enabled}
            deliveryEnabled={info.orders_delivery_enabled}
            tableNumber={tableNumber}
            onClose={() => setCheckoutOpen(false)}
            onSuccess={(num) => { setOrderConfirmed({ number: num }); setCart([]); setCheckoutOpen(false) }}
          />
        )}

        {/* Order confirmation */}
        {orderConfirmed && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 anim-fadein">
            <div className="absolute inset-0 bg-black/40" onClick={() => setOrderConfirmed(null)} />
            <div className="relative bg-white rounded-3xl p-8 max-w-sm w-full text-center anim-slideup">
              <div className="mx-auto mb-4 w-16 h-16 rounded-full flex items-center justify-center" style={{ background: accent + '22' }}>
                <Check className="h-8 w-8" style={{ color: accent }} />
              </div>
              <h2 className="menu-display text-2xl font-bold mb-2">Bestellung eingegangen!</h2>
              {orderConfirmed.number > 0 && (
                <>
                  <p className="text-sm text-neutral-500 mb-1">Bestellnummer</p>
                  <p className="menu-display text-3xl font-bold mb-4" style={{ color: accent }}>#{orderConfirmed.number}</p>
                </>
              )}
              <p className="text-xs text-neutral-500 mb-6">Wir bereiten Ihre Bestellung vor und melden uns telefonisch.</p>
              <button type="button" onClick={() => setOrderConfirmed(null)}
                className="w-full rounded-xl py-3 font-semibold text-sm text-white"
                style={{ background: accent }}>
                Schließen
              </button>
            </div>
          </div>
        )}

        {/* Bottom category nav — replaces the old phone/Bewerten/powered-by
            footer: guests navigate the menu with their thumb (mobile
            ergonomics) and the promo line is gone, per owner request. */}
        {/* Green bar + white pills, slightly bolder type — per owner request
            ("mobilde alt menüde yeşil-beyaz, bi tık kalın"), in REZARO's own
            brand green. Shown on EVERY view (home, Hauptkategorie, list) and
            the ⌂ pill is PINNED left — it never scrolls away with the pills
            (both also per owner request). */}
        {/* Hidden on the LANDING (the main-category grid) — it appears once
            the guest steps into a Hauptkategorie, and its pills are that
            group's sub-categories (owner request). Classic design unchanged. */}
        {menu.length > 0 && loaded && (!isCards || cardsView !== 'home') && (
          <div className="fixed inset-x-0 z-20 px-4"
            style={{ bottom: 'calc(env(safe-area-inset-bottom) + 12px)' }}>
            {/* Floating fully-rounded bar (owner-supplied mockup): inset from
                the screen edges, white home pill with a house icon pinned
                left, borderless white category labels, right-edge fade. */}
            <div className="max-w-2xl mx-auto flex items-center gap-1.5 rounded-full pl-1.5 pr-1 py-1.5 shadow-xl"
              style={{ background: accent }}>
              {isCards && (
                <button type="button"
                  aria-label={ct.overview}
                  onClick={() => {
                    // In a category list that was reached through a
                    // Hauptkategorie, the arrow goes back up to it;
                    // everywhere else it returns to the landing grid.
                    if (isGrouped && activeGroupData && cardsView === 'list') setCardsView('group')
                    else { setActiveGroup(null); setCardsView('home') }
                    window.scrollTo({ top: 0 })
                  }}
                  className="cat-pill flex-shrink-0 flex items-center justify-center h-9 w-9 rounded-full shadow-md"
                  style={{ background: '#fff', color: accent }}>
                  <ArrowLeft className="h-[19px] w-[19px]" strokeWidth={2.7} />
                </button>
              )}
              <div className="flex items-center gap-1 overflow-x-auto no-scrollbar flex-1"
                style={{ maskImage: 'linear-gradient(90deg, #000 88%, transparent)', WebkitMaskImage: 'linear-gradient(90deg, #000 88%, transparent)' }}>
                {isGrouped ? (
                  // Grouped design: the strip holds the HAUPTKATEGORIEN (owner
                  // request) — tapping one jumps to that group's screen.
                  <>
                    {groups.map((g) => {
                      const isActive = activeGroupData?.id === g.id
                      return (
                        <button key={g.id} type="button"
                          onClick={() => { setActiveGroup(g.id); setCardsView('group'); window.scrollTo({ top: 0 }) }}
                          className={`cat-pill flex-shrink-0 text-[13px] px-3.5 py-2 rounded-full whitespace-nowrap ${isActive ? 'active font-bold' : 'font-semibold'}`}
                          style={{
                            background: isActive ? '#fff' : 'transparent',
                            color: isActive ? accent : 'rgba(255,255,255,0.95)',
                          }}>
                          {tGroupName(g.id, g.name)}
                        </button>
                      )
                    })}
                    {standaloneCats.map((cat) => {
                      const isActive = !activeGroupData && activeCat === cat.id && cardsView === 'list'
                      return (
                        <button key={cat.id} type="button"
                          onClick={() => openCategory(cat.id)}
                          className={`cat-pill flex-shrink-0 text-[13px] px-3.5 py-2 rounded-full whitespace-nowrap ${isActive ? 'active font-bold' : 'font-semibold'}`}
                          style={{
                            background: isActive ? '#fff' : 'transparent',
                            color: isActive ? accent : 'rgba(255,255,255,0.95)',
                          }}>
                          {tCatName(cat.id, cat.name)}
                        </button>
                      )
                    })}
                  </>
                ) : listCats.map((cat) => {
                  const isActive = activeCat === cat.id && (!isCards || cardsView === 'list')
                  return (
                    <a
                      key={cat.id}
                      id={`catpill-${cat.id}`}
                      href={`#cat-${cat.id}`}
                      onClick={(e) => {
                        // On the card/group screens the sections aren't in the
                        // DOM — navigate into the category instead of anchoring.
                        if (isCards && cardsView !== 'list') { e.preventDefault(); openCategory(cat.id) }
                        else setActiveCat(cat.id)
                      }}
                      className={`cat-pill flex-shrink-0 text-[13px] px-3.5 py-2 rounded-full whitespace-nowrap ${isActive ? 'active font-bold' : 'font-semibold'}`}
                      style={{
                        background: isActive ? '#fff' : 'transparent',
                        color: isActive ? accent : 'rgba(255,255,255,0.95)',
                      }}
                    >
                      {tCatName(cat.id, cat.name)}
                    </a>
                  )
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function CheckoutSheet({
  restaurantId, cart, cartSubtotal, accent, settings,
  pickupEnabled, deliveryEnabled, tableNumber, onClose, onSuccess,
}: {
  restaurantId: string
  cart: CartLine[]
  cartSubtotal: number
  accent: string
  settings: OrdersSettings
  pickupEnabled: boolean
  deliveryEnabled: boolean
  tableNumber?: string | null
  onClose: () => void
  onSuccess: (orderNumber: number) => void
}) {
  const dineIn = !!tableNumber
  const initialType: OrderType = dineIn ? 'dinein' : pickupEnabled ? 'pickup' : 'delivery'
  const [type, setType] = useState<OrderType>(initialType)
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [address, setAddress] = useState('')
  const [notes, setNotes] = useState('')
  const [paymentMethod, setPaymentMethod] = useState('cash')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const enabledPaymentMethods = useMemo(() => {
    const m = settings.payment_methods
    return [
      { key: 'cash', label: 'Bar', enabled: m.cash },
      { key: 'card', label: 'Kreditkarte', enabled: m.card },
      { key: 'paypal', label: 'PayPal', enabled: m.paypal },
      { key: 'sepa', label: 'SEPA', enabled: m.sepa },
    ].filter(x => x.enabled)
  }, [settings])

  const deliveryFee = type === 'delivery' ? settings.delivery_fee : 0
  const total = cartSubtotal + deliveryFee
  const meetsMinimum = type !== 'delivery' || cartSubtotal >= settings.min_order_amount

  async function submit() {
    setError(null)
    if (!name.trim()) { setError('Bitte Name angeben'); return }
    if (!dineIn && !phone.trim()) { setError('Bitte Telefonnummer angeben'); return }
    if (type === 'delivery' && !address.trim()) { setError('Bitte Lieferadresse angeben'); return }
    if (!meetsMinimum) { setError(`Mindestbestellwert: ${settings.min_order_amount.toFixed(2)} €`); return }
    if (cart.length === 0) { setError('Warenkorb ist leer'); return }

    setSubmitting(true)
    try {
      const supabase = createClient()
      const items: OrderItem[] = cart.map(c => ({
        id: c.itemId, name: c.name, price: c.price, quantity: c.quantity,
      }))
      const { error: err } = await createOrder(supabase, {
        restaurant_id: restaurantId,
        type,
        customer_name: name.trim(),
        customer_phone: phone.trim() || undefined,
        customer_email: email.trim() || undefined,
        address: type === 'delivery' ? address.trim() : undefined,
        items,
        subtotal: cartSubtotal,
        delivery_fee: deliveryFee,
        total,
        payment_method: dineIn ? 'cash' : paymentMethod,
        notes: notes.trim() || undefined,
        table_number: dineIn ? tableNumber! : undefined,
      })
      if (err) { setError(err); return }
      // Anonymous INSERT has no SELECT permission on orders, so we can't
      // return the order_number from the DB. Show a generic confirmation
      // (the restaurant sees the new order in real-time on /orders admin).
      onSuccess(0)
    } finally { setSubmitting(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center anim-fadein">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-t-3xl p-6 anim-slideup max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="menu-display text-2xl font-bold">Bestellung abschließen</h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {dineIn ? (
          <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
            <p className="text-sm font-semibold text-emerald-900">🍽️ Bestellung für Tisch {tableNumber}</p>
            <p className="text-xs text-emerald-700 mt-0.5">Bezahlung bar am Tisch — bequem beim Service.</p>
          </div>
        ) : pickupEnabled && deliveryEnabled && (
          <div className="flex gap-1 rounded-xl bg-neutral-100 p-1 mb-4">
            <button type="button" onClick={() => setType('pickup')}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${type === 'pickup' ? 'bg-white shadow text-neutral-900' : 'text-neutral-500'}`}>
              Abholung
            </button>
            <button type="button" onClick={() => setType('delivery')}
              className={`flex-1 rounded-lg py-2 text-sm font-medium transition-colors ${type === 'delivery' ? 'bg-white shadow text-neutral-900' : 'text-neutral-500'}`}>
              Lieferung
            </button>
          </div>
        )}

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-neutral-700">Name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Vor- und Nachname"
                className="mt-1 h-9 w-full rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-700">Telefon{dineIn ? '' : ' *'}</label>
              <input value={phone} onChange={(e) => setPhone(e.target.value)} type="tel"
                placeholder={dineIn ? 'optional' : '+49…'}
                className="mt-1 h-9 w-full rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-neutral-700">E-Mail</label>
            <input value={email} onChange={(e) => setEmail(e.target.value)} type="email"
              placeholder="optional"
              className="mt-1 h-9 w-full rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2" />
          </div>
          {type === 'delivery' && (
            <div>
              <label className="text-xs font-medium text-neutral-700">Lieferadresse *</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)}
                placeholder="Straße, PLZ, Stadt"
                className="mt-1 h-9 w-full rounded-lg border border-neutral-300 px-3 text-sm focus:outline-none focus:ring-2" />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-neutral-700">Anmerkungen</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Allergien, Sonderwünsche…"
              rows={2}
              className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2" />
          </div>

          {!dineIn && enabledPaymentMethods.length > 0 && (
            <div>
              <label className="text-xs font-medium text-neutral-700">Zahlung</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {enabledPaymentMethods.map(pm => (
                  <label key={pm.key}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${paymentMethod === pm.key ? 'border-2' : 'border-neutral-200'}`}
                    style={paymentMethod === pm.key ? { borderColor: accent } : undefined}>
                    <input type="radio" name="pm" value={pm.key} checked={paymentMethod === pm.key}
                      onChange={() => setPaymentMethod(pm.key)} />
                    <span>{pm.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="mt-5 space-y-1.5 text-sm border-t pt-4">
          <div className="flex justify-between text-neutral-600">
            <span>Zwischensumme</span><span>{fmtPrice(cartSubtotal)}</span>
          </div>
          {deliveryFee > 0 && (
            <div className="flex justify-between text-neutral-600">
              <span>Lieferung</span><span>{fmtPrice(deliveryFee)}</span>
            </div>
          )}
          <div className="flex justify-between text-base font-bold pt-1.5 border-t mt-1.5">
            <span>Gesamt</span><span>{fmtPrice(total)}</span>
          </div>
          {!meetsMinimum && (
            <p className="text-xs text-red-600 mt-2">
              Mindestbestellwert für Lieferung: {fmtPrice(settings.min_order_amount)}
            </p>
          )}
        </div>

        {error && (
          <p className="mt-3 text-xs text-red-600 bg-red-50 p-2 rounded">{error}</p>
        )}

        <button type="button" onClick={submit} disabled={submitting || !meetsMinimum}
          className="mt-4 w-full rounded-xl py-3 text-white font-semibold text-sm disabled:opacity-50"
          style={{ background: accent }}>
          {submitting ? 'Wird gesendet…' : `Jetzt bestellen · ${fmtPrice(total)}`}
        </button>
      </div>
    </div>
  )
}
